import { debounce } from "../lib/ui-helpers.ts";

type ProductModelElement = HTMLElement & {
  modelViewerUI?: {
    pause: () => void;
  };
};

export const defineMediaGalleryCustomElement = (): void => {
  if (customElements.get("media-gallery")) return;

  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      elements: any;
      mql: MediaQueryList;
      stickyHeader: Element | null;

      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia("(min-width: 750px)");
        this.stickyHeader = null;
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener("slideChanged", debounce(this.onSlideChanged.bind(this), 100));
        this.elements.thumbnails.querySelectorAll("[data-target]").forEach((mediaToSwitch: any) => {
          mediaToSwitch.querySelector("button").addEventListener("click", this.setActiveMedia.bind(this, (mediaToSwitch as any).dataset.target, false));
        });

        if (this.elements.viewer?.hasAttribute("data-external-nav")) {
          const getMediaOrder = (): string[] =>
            Array.from(this.elements.thumbnails?.querySelectorAll("[data-target]") ?? [])
              .map((t: any) => t.dataset.target)
              .filter(Boolean) as string[];

          const getActiveMediaId = (): string =>
            this.elements.viewer?.querySelector("[data-media-id].is-active")?.dataset.mediaId ?? "";

          const updateNavButtons = (order: string[], activeIndex: number) => {
            const prevBtn = this.elements.viewer?.querySelector('button[name="previous"]');
            const nextBtn = this.elements.viewer?.querySelector('button[name="next"]');
            activeIndex <= 0 ? prevBtn?.setAttribute("disabled", "disabled") : prevBtn?.removeAttribute("disabled");
            activeIndex >= order.length - 1 ? nextBtn?.setAttribute("disabled", "disabled") : nextBtn?.removeAttribute("disabled");
          };

          this.elements.viewer?.querySelector('button[name="previous"]')?.addEventListener("click", () => {
            const order = getMediaOrder();
            const i = order.indexOf(getActiveMediaId());
            const id = order[i - 1];
            if (id) { this.setActiveMedia(id, false); updateNavButtons(order, i - 1); }
          });

          this.elements.viewer?.querySelector('button[name="next"]')?.addEventListener("click", () => {
            const order = getMediaOrder();
            const i = order.indexOf(getActiveMediaId());
            const id = order[i + 1];
            if (id) { this.setActiveMedia(id, false); updateNavButtons(order, i + 1); }
          });

          window.setTimeout(() => {
            const o = getMediaOrder();
            updateNavButtons(o, o.indexOf(getActiveMediaId()));
          });
        }

        if (this.dataset.desktopLayout !== "stacked" && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event: any) {
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${event.detail.currentElement?.dataset.mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId: string, prepend: boolean) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) as HTMLElement | null;

        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll("[data-media-id]").forEach((element: any) => {
          element.classList.remove("is-active");
          element.removeAttribute("mobile-visible");
        });
        activeMedia.classList.add("is-active");
        activeMedia.setAttribute("mobile-visible", "");

        if (prepend) {
          activeMedia.parentElement?.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail?.parentElement?.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        if (!this.elements.viewer?.hasAttribute("data-external-nav")) {
          window.setTimeout(() => {
            if (this.elements.thumbnails) {
              activeMedia.parentElement?.scrollTo({
                left: activeMedia ? activeMedia.offsetLeft : 0,
              });
            }
            if (!this.elements.thumbnails || this.dataset.desktopLayout === "stacked") {
              activeMedia.scrollIntoView({ behavior: "smooth" });
            }
          });
        }
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, (activeThumbnail as any)?.dataset.mediaPosition);

        if (this.elements.viewer?.hasAttribute("data-external-nav")) {
          const order = Array.from(this.elements.thumbnails.querySelectorAll("[data-target]"))
            .map((t: any) => t.dataset.target)
            .filter(Boolean) as string[];
          const activeIndex = order.indexOf(mediaId);
          if (activeIndex !== -1) {
            const prevBtn = this.elements.viewer?.querySelector('button[name="previous"]');
            const nextBtn = this.elements.viewer?.querySelector('button[name="next"]');
            activeIndex <= 0 ? prevBtn?.setAttribute("disabled", "disabled") : prevBtn?.removeAttribute("disabled");
            activeIndex >= order.length - 1 ? nextBtn?.setAttribute("disabled", "disabled") : nextBtn?.removeAttribute("disabled");
          }
        }
      }

      setActiveThumbnail(thumbnail: any) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails.querySelectorAll("button").forEach((element: any) => element.removeAttribute("aria-current"));
        thumbnail.querySelector("button")?.setAttribute("aria-current", "true");

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({
          left: thumbnail ? thumbnail.offsetLeft : 0,
        });
      }

      announceLiveRegion(activeItem: any, position: any) {
        const image = activeItem.querySelector(".product__modal-opener--image img") as HTMLImageElement | null;
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute("aria-hidden", false);
          this.elements.liveRegion.innerHTML = (window.accessibilityStrings?.imageAvailable ?? "").replace("[index]", position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute("aria-hidden", true);
          }, 2000);
        };
        image.src = image.src;
      }

      pauseAllMedia() {
        document.querySelectorAll<HTMLIFrameElement>(".js-youtube").forEach((video) => {
          video.contentWindow?.postMessage('{"event":"command","func":"' + "pauseVideo" + '","args":""}', "*");
        });
        document.querySelectorAll<HTMLIFrameElement>(".js-vimeo").forEach((video) => {
          video.contentWindow?.postMessage('{"method":"pause"}', "*");
        });
        document.querySelectorAll<HTMLVideoElement>("video").forEach((video) => video.pause());
        document.querySelectorAll<ProductModelElement>("product-model").forEach((model) => model.modelViewerUI?.pause());
      }

      playActiveMedia(activeItem: any) {
        this.pauseAllMedia();
        const deferredMedia = activeItem.querySelector(".deferred-media");
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector("sticky-header");
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute("role", "presentation");
        this.elements.viewer.sliderItems.forEach((slide: any) => slide.setAttribute("role", "presentation"));
      }
    },
  );
};
